import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, isRetryable } from "./api";

export type SaveState = "idle" | "saving" | "saved" | "error";

export interface Autosave<V = unknown> {
  /** Overall state for the live region. */
  state: SaveState;
  /** Items whose last save failed, with the value we still hold for them. */
  failed: Record<string, V>;
  /** True while any save is queued or in flight. */
  pending: boolean;
  /** Error message for the most recent failure. */
  message: string | null;
  save: (itemCode: string, value: V) => void;
  retry: () => void;
}

/** A failed save that may work next time (no connection, a server error, a busy server) is tried again by itself after this long, doubling, up to AUTO_RETRY_MAX_MS. */
export const AUTO_RETRY_FIRST_MS = 3_000;
export const AUTO_RETRY_MAX_MS = 30_000;

/**
 * Saves each answer as soon as it is picked. Saves are serialized per item and the latest
 * value wins, so a quick change of mind can't land out of order. A failed save keeps the
 * choice on screen and can be retried; one that may work next time is also tried again by
 * itself, every few seconds (Oct 27: a server restart in the middle of an event shouldn't need
 * 800 people to notice and tap Try again).
 */
export function useAutosave<V>(
  send: (itemCode: string, value: V) => Promise<unknown>,
  /** Called when the server says answers are locked (already submitted). */
  onLocked: () => void,
  lockedCode = "take_completed",
): Autosave<V> {
  const queued = useRef(new Map<string, V>());
  const inFlight = useRef(new Set<string>());
  const [failed, setFailed] = useState<Record<string, V>>({});
  const [state, setState] = useState<SaveState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(0);
  /** Whether the latest failure may work if tried again, and how long the next wait is. */
  const transient = useRef(false);
  const backoff = useRef(AUTO_RETRY_FIRST_MS);
  const mounted = useRef(true);
  const sendRef = useRef(send);
  sendRef.current = send;
  const onLockedRef = useRef(onLocked);
  onLockedRef.current = onLocked;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (itemCode: string) => {
      if (inFlight.current.has(itemCode)) return;
      inFlight.current.add(itemCode);
      try {
        while (queued.current.has(itemCode)) {
          const value = queued.current.get(itemCode)!;
          queued.current.delete(itemCode);
          try {
            await sendRef.current(itemCode, value);
            if (!mounted.current) return;
            setFailed((f) => {
              if (!(itemCode in f)) return f;
              const { [itemCode]: _, ...rest } = f;
              return rest;
            });
          } catch (e) {
            if (!mounted.current) return;
            if (e instanceof ApiError && e.code === lockedCode) {
              onLockedRef.current();
              return;
            }
            if (!queued.current.has(itemCode)) {
              transient.current = isRetryable(e) || (e instanceof ApiError && e.status === 429);
              setFailed((f) => ({ ...f, [itemCode]: value }));
              setMessage(
                e instanceof ApiError && e.status === 0
                  ? "Your answer didn't save. Check your connection and try again."
                  : "Your answer didn't save. Try again.",
              );
            }
          }
        }
      } finally {
        inFlight.current.delete(itemCode);
        if (mounted.current) setBusy((n) => n - 1);
      }
    },
    [lockedCode],
  );

  const save = useCallback(
    (itemCode: string, value: V) => {
      queued.current.set(itemCode, value);
      if (!inFlight.current.has(itemCode)) {
        setBusy((n) => n + 1);
        void run(itemCode);
      }
    },
    [run],
  );

  const retry = useCallback(() => {
    setMessage(null);
    for (const [itemCode, value] of Object.entries(failed)) save(itemCode, value);
  }, [failed, save]);

  const failedCount = Object.keys(failed).length;
  // Try failed saves again by themselves while it may help: 3s, 6s, 12s ... 30s apart.
  useEffect(() => {
    if (failedCount === 0) {
      backoff.current = AUTO_RETRY_FIRST_MS;
      return;
    }
    if (busy > 0 || !transient.current) return;
    const wait = backoff.current;
    const timer = setTimeout(() => {
      backoff.current = Math.min(AUTO_RETRY_MAX_MS, wait * 2);
      retry();
    }, wait);
    return () => clearTimeout(timer);
  }, [failedCount, busy, retry]);
  useEffect(() => {
    if (busy > 0) setState("saving");
    else if (failedCount > 0) setState("error");
    else setState((s) => (s === "saving" ? "saved" : s));
  }, [busy, failedCount]);

  return { state, failed, pending: busy > 0, message, save, retry };
}
